import { PetData, PetTreatData, PetsData } from "../data/pets";

/**
 * Pets v2 between battles (docs/PETS.md): the pets the player has caught (explicit now) and each one's level, the pet
 * that comes into battles (or none), the Pet Treats held and the one armed for the next battle, and the run keys whose
 * catch was paid and whose treat was used (each granted once). Kept on the PlayerProfile (`profile.pets`, like the
 * supplies) so the pet's upgrades ride the towers' upgrade system (meta/profile.ts UpgradeTarget.Pet); saved in the
 * `pets` save section by @highmarch/app. Pure.
 *
 * Migration from v1 (Bond XP; the pup "had" once level `v1UnlockAfterLevel` was cleared, derived, never stored): a v1
 * section (no `v`), or no section at all on a save past that level, owns the pup at level 1; v1's "none" (going
 * without) is kept; Bond XP and the credited runs are dropped.
 */

export const PETS_SECTION_VERSION = 2;
/** How many run keys are kept to refuse a repeat grant (a run's catch and treat are settled within the run). */
const KEYS_KEPT = 40;

export interface PetsJson {
  v: 2;
  /** Pet id → its level (1 … the towers' max). */
  owned: Record<string, number>;
  /** The pet taken into battles: its id, or "none" to go without; absent: the first pet owned. */
  equipped?: string;
  /** Pet Treat id → how many are held. */
  treats?: Record<string, number>;
  /** The treat armed for the next battle. */
  armed?: string;
  /** Run keys whose catch was paid / whose treat was used (newest last). */
  caught?: string[];
  treatUsed?: string[];
}

/** A v1 `pets` section (read once, by the migration). */
export interface PetsJsonV1 { xp?: Record<string, number>; equipped?: string; credited?: string[] }

export type TreatArmResult = "armed" | "disarmed" | "none" | "unknown";

const obj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const keys = (v: unknown): string[] => (Array.isArray(v) ? v.filter((k): k is string => typeof k === "string").slice(-KEYS_KEPT) : []);

export class PetsState {
  private readonly levels = new Map<string, number>();
  private equippedId: string | null = null;
  private goneWithout = false;
  private readonly treats = new Map<string, number>();
  private armedId: string | null = null;
  private readonly caughtKeys: string[] = [];
  private readonly treatKeys: string[] = [];

  constructor(readonly data: PetsData, private readonly maxLevel = Number.MAX_SAFE_INTEGER) {}

  /**
   * The save's section, migrated: a v2 section as stored (unknown ids, bad levels and counts dropped, field by field);
   * a v1 section, or none on a save that cleared `v1UnlockAfterLevel`, as v1 had it (the pup at level 1); else empty.
   */
  static fromJson(data: PetsData, j: unknown, highestCleared: number, maxLevel = Number.MAX_SAFE_INTEGER): PetsState {
    const s = new PetsState(data, maxLevel);
    s.restore(j, highestCleared);
    return s;
  }

  /** Replaces this state with the save's section, migrated (see fromJson). */
  restore(j: unknown, highestCleared: number): void {
    this.levels.clear(); this.treats.clear(); this.caughtKeys.length = 0; this.treatKeys.length = 0;
    this.equippedId = null; this.goneWithout = false; this.armedId = null;
    if (obj(j) && j.v === PETS_SECTION_VERSION) { this.load(j as Partial<PetsJson>); return; }
    const v1 = obj(j) ? (j as PetsJsonV1) : undefined;
    // v1 had one pet, the pup (the roster's first): only it was ever had
    const pup = this.data.list[0];
    if (pup !== undefined && highestCleared >= this.data.v1UnlockAfterLevel) this.levels.set(pup.id, 1);
    if (v1?.equipped === "none") this.goneWithout = true;
    else if (typeof v1?.equipped === "string" && this.levels.has(v1.equipped)) this.equippedId = v1.equipped;
  }

  private load(j: Partial<PetsJson>): void {
    const known = new Set(this.data.list.map((p) => p.id));
    if (obj(j.owned)) for (const [id, l] of Object.entries(j.owned)) if (known.has(id) && Number.isInteger(l) && (l as number) >= 1) this.levels.set(id, Math.min(this.maxLevel, l as number));
    if (j.equipped === "none") this.goneWithout = true;
    else if (typeof j.equipped === "string" && this.levels.has(j.equipped)) this.equippedId = j.equipped;
    const treatIds = new Set(this.data.treats.map((t) => t.id));
    if (obj(j.treats)) for (const [id, n] of Object.entries(j.treats)) if (treatIds.has(id) && Number.isInteger(n) && (n as number) > 0) this.treats.set(id, Math.min(this.data.treatMaxOwned, n as number));
    if (typeof j.armed === "string" && this.treatCount(j.armed) > 0) this.armedId = j.armed;
    this.caughtKeys.push(...keys(j.caught));
    this.treatKeys.push(...keys(j.treatUsed));
  }

  toJson(): PetsJson {
    const owned: Record<string, number> = {};
    for (const p of this.data.list) { const l = this.levels.get(p.id); if (l !== undefined) owned[p.id] = l; }
    const out: PetsJson = { v: PETS_SECTION_VERSION, owned };
    if (this.goneWithout) out.equipped = "none";
    else if (this.equippedId !== null) out.equipped = this.equippedId;
    const treats: Record<string, number> = {};
    for (const t of this.data.treats) { const n = this.treatCount(t.id); if (n > 0) treats[t.id] = n; }
    if (Object.keys(treats).length > 0) out.treats = treats;
    if (this.armedId !== null) out.armed = this.armedId;
    if (this.caughtKeys.length > 0) out.caught = [...this.caughtKeys];
    if (this.treatKeys.length > 0) out.treatUsed = [...this.treatKeys];
    return out;
  }

  // ---- the collection and levels

  owns(id: string): boolean { return this.levels.has(id); }
  /** The pets owned, in roster order. */
  owned(): readonly PetData[] { return this.data.list.filter((p) => this.levels.has(p.id)); }
  /** A pet's level, 0 when not owned. */
  levelOf(id: string): number { return this.levels.get(id) ?? 0; }
  /** By roster index (the upgrade system's `towerIndex` for UpgradeTarget.Pet): 0 when not owned or no such pet. */
  levelAt(index: number): number { const p = this.data.list[index]; return p === undefined ? 0 : this.levelOf(p.id); }
  /** Sets an owned pet's level (the upgrade system, a catch): never down, never past the max. */
  raiseLevelAt(index: number, level: number): void {
    const p = this.data.list[index];
    if (p === undefined || !this.levels.has(p.id)) return;
    this.levels.set(p.id, Math.min(this.maxLevel, Math.max(this.levelOf(p.id), level)));
  }
  /** The first catch: the pet is owned at level 1 (and comes into battles unless the player goes without). */
  unlock(id: string): boolean {
    if (this.levels.has(id) || !this.data.list.some((p) => p.id === id)) return false;
    this.levels.set(id, 1);
    return true;
  }

  /** The pet that comes into battles, or null (none owned, or the player goes without). */
  equipped(): PetData | null {
    if (this.goneWithout) return null;
    const owned = this.owned();
    return owned.find((p) => p.id === this.equippedId) ?? owned[0] ?? null;
  }
  /** Take `id` into battles (a pet owned), or go without (null). */
  equip(id: string | null): boolean {
    if (id === null) { this.goneWithout = true; return true; }
    if (!this.levels.has(id)) return false;
    this.equippedId = id; this.goneWithout = false;
    return true;
  }

  // ---- Pet Treats

  treatCount(id: string): number { return this.treats.get(id) ?? 0; }
  /** Adds up to `n`, never past `treatMaxOwned`; returns how many were added. */
  addTreat(id: string, n: number): number {
    if (!this.data.treats.some((t) => t.id === id) || !Number.isInteger(n) || n <= 0) return 0;
    const before = this.treatCount(id), after = Math.min(this.data.treatMaxOwned, before + n);
    this.treats.set(id, after);
    return after - before;
  }
  /** The treat armed for the next battle (one at most), or null. */
  armedTreat(): PetTreatData | null { return this.armedId === null ? null : this.data.treats.find((t) => t.id === this.armedId) ?? null; }
  /** The battle card's tap: arm `id` (one held; it replaces another armed), disarm it (armed already), or `null` to disarm. */
  toggleTreat(id: string | null): TreatArmResult {
    if (id === null || id === this.armedId) { const was = this.armedId !== null; this.armedId = null; return was ? "disarmed" : "none"; }
    if (!this.data.treats.some((t) => t.id === id)) return "unknown";
    if (this.treatCount(id) <= 0) return "none";
    this.armedId = id;
    return "armed";
  }
  /**
   * A wild pet appeared in run `key` with treat `id` armed for it: one leaves the stock, once per key (a resumed or
   * retried battle that replays the appearance takes nothing again). It is disarmed: each battle is armed afresh, like
   * the supplies.
   */
  useTreat(id: string, key: string): boolean {
    if (this.treatKeys.includes(key) || this.treatCount(id) <= 0) return false;
    this.remember(this.treatKeys, key);
    const left = this.treatCount(id) - 1;
    if (left > 0) this.treats.set(id, left); else this.treats.delete(id);
    if (this.armedId === id) this.armedId = null;
    return true;
  }
  treatUsedOn(key: string): boolean { return this.treatKeys.includes(key); }

  // ---- catches

  catchPaid(key: string): boolean { return this.caughtKeys.includes(key); }
  /** Notes run `key`'s catch as paid (the caller pays it). False when it was already. */
  noteCatch(key: string): boolean {
    if (this.caughtKeys.includes(key)) return false;
    this.remember(this.caughtKeys, key);
    return true;
  }

  private remember(list: string[], key: string): void {
    list.push(key);
    if (list.length > KEYS_KEPT) list.splice(0, list.length - KEYS_KEPT);
  }
}
